import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { toast } from "sonner";
import {
  X, Loader2, Search, ChevronDown, CheckSquare, Square,
  Lock, FileSpreadsheet, Layers,
} from "lucide-react";

// ─── Selector de Frente (agrupado por frente padre, con búsqueda) ─────────────
function FrenteSelect({ frentes, value, onChange, compact = false, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const ql = q.trim().toLowerCase();
  const groups = useMemo(() => frentes.map((f) => ({
    id: f._id || f.id,
    parent: f.presupuesto,
    subs: (f.frentes || []).filter((s) => !ql || s.toLowerCase().includes(ql) || (f.presupuesto || "").toLowerCase().includes(ql)),
  })).filter((g) => g.subs.length > 0), [frentes, ql]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-1 rounded-lg border text-left transition-colors ${
          value?.frente_label
            ? "bg-pink-50 border-pink-200 text-pink-800"
            : "bg-white border-gray-200 text-gray-400 hover:border-gray-300"
        } ${compact ? "px-2 py-1 text-xs" : "px-2.5 py-1.5 text-xs"}`}
      >
        <span className="truncate">{value?.frente_label || "Asignar frente…"}</span>
        <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
      </button>
      {open && (
        <div className="absolute z-[10001] mt-1 w-72 max-h-80 overflow-auto bg-white rounded-xl shadow-2xl border border-gray-200 right-0">
          <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2 py-1.5">
              <Search className="w-3.5 h-3.5 text-gray-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar frente…"
                className="bg-transparent text-xs outline-none flex-1"
                data-testid="frente-search-input"
              />
            </div>
          </div>
          <button
            onClick={() => { onChange(null); setOpen(false); setQ(""); }}
            className="w-full text-left px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 border-b border-gray-50"
          >
            — Sin frente —
          </button>
          {groups.map((g) => (
            <div key={g.id}>
              <div className="px-3 py-1.5 bg-gray-50 text-[10px] font-bold uppercase tracking-wide text-gray-500 sticky">
                {g.parent}
              </div>
              {g.subs.map((s) => (
                <button
                  key={g.id + s}
                  data-testid={`frente-option-${s}`}
                  onClick={() => { onChange({ frente_id: g.id, frente_label: s }); setOpen(false); setQ(""); }}
                  className={`w-full text-left px-4 py-2 text-xs hover:bg-pink-50 transition-colors ${
                    value?.frente_id === g.id && value?.frente_label === s ? "bg-pink-50 text-pink-800 font-semibold" : "text-gray-700"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          ))}
          {groups.length === 0 && <div className="px-3 py-4 text-xs text-gray-400 text-center">Sin resultados</div>}
        </div>
      )}
    </div>
  );
}

const COLS = [
  { key: "numero", label: "N°", w: "w-[56px]", align: "left" },
  { key: "descripcion", label: "DESCRIPCIÓN", w: "min-w-[240px]", align: "left" },
  { key: "unidad", label: "Un.", w: "w-[56px]", align: "center" },
  { key: "cantidad", label: "Cant.", w: "w-[64px]", align: "right", num: true },
  { key: "costo_unit_mat", label: "MAT", w: "w-[96px]", align: "right", cur: true },
  { key: "costo_unit_mo", label: "MDO", w: "w-[96px]", align: "right", cur: true },
  { key: "pu_mat", label: "PU MAT", w: "w-[96px]", align: "right", cur: true },
  { key: "pu_mo", label: "PU MDO", w: "w-[96px]", align: "right", cur: true },
  { key: "total_final", label: "MONT", w: "w-[110px]", align: "right", cur: true },
];

export default function FormateoTablaModal({ budgetId, onClose, onClosed, frentes, readOnly = false }) {
  const [budget, setBudget] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkFrente, setBulkFrente] = useState(null);
  const pendingRef = useRef({});       // item_index -> {frente_id, frente_label|null}
  const timerRef = useRef(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [budgetId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/purchases/formateo/${budgetId}`);
      setBudget(data);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el presupuesto");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const flush = async () => {
    const pending = pendingRef.current;
    const keys = Object.keys(pending);
    if (keys.length === 0) return;
    pendingRef.current = {};
    const assignments = keys.map((k) => ({
      item_index: Number(k),
      frente_id: pending[k]?.frente_id || null,
      frente_label: pending[k]?.frente_label || "",
    }));
    setSaving(true);
    try {
      await axios.put(`${API}/purchases/formateo/${budgetId}/items-frente`, { assignments });
    } catch (e) {
      toast.error("Error al guardar frente");
    } finally {
      setSaving(false);
    }
  };

  const scheduleSave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 1000);
  };

  const assignOne = (idx, val) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], frente_id: val?.frente_id || null, frente_label: val?.frente_label || "" };
      return copy;
    });
    pendingRef.current[idx] = val ? { frente_id: val.frente_id, frente_label: val.frente_label } : { frente_id: null, frente_label: "" };
    scheduleSave();
  };

  const applyBulk = () => {
    if (!bulkFrente || selected.size === 0) return;
    setItems((prev) => {
      const copy = [...prev];
      selected.forEach((idx) => {
        copy[idx] = { ...copy[idx], frente_id: bulkFrente.frente_id, frente_label: bulkFrente.frente_label };
        pendingRef.current[idx] = { frente_id: bulkFrente.frente_id, frente_label: bulkFrente.frente_label };
      });
      return copy;
    });
    scheduleSave();
    toast.success(`Frente aplicado a ${selected.size} ítem(s)`);
    setSelected(new Set());
  };

  const toggleSel = (idx) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(idx) ? n.delete(idx) : n.add(idx);
      return n;
    });
  };

  const selectableIdx = items.map((it, i) => (it.is_group ? null : i)).filter((i) => i !== null);
  const allSelected = selectableIdx.length > 0 && selectableIdx.every((i) => selected.has(i));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIdx));
  };

  const nonGroup = items.filter((it) => !it.is_group);
  const assignedCount = nonGroup.filter((it) => it.frente_id).length;
  const missingCount = nonGroup.length - assignedCount;

  const handleCerrar = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await flush();
    if (missingCount > 0) {
      const ok = window.confirm(`Tenés ${missingCount} ítem(s) sin frente asignado. ¿Querés cerrar el formato igual?`);
      if (!ok) return;
    }
    setClosing(true);
    try {
      await axios.post(`${API}/purchases/formateo/${budgetId}/cerrar`);
      toast.success("Formato cerrado correctamente");
      onClosed && onClosed();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cerrar el formato");
    } finally {
      setClosing(false);
    }
  };

  const handleBack = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await flush();
    onClosed && onClosed();
    onClose();
  };

  const exportExcel = async () => {
    try {
      const { data } = await axios.get(`${API}/purchases/formateo/${budgetId}/export-excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Formato_${budget?.number || "presupuesto"}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar el Excel");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex flex-col bg-gray-50" data-testid="formateo-tabla-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-3 min-w-0">
          <Layers className="w-5 h-5 text-pink-600 flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="font-bold text-gray-900 truncate">
              Formato — {budget?.nombre || "…"}
            </h2>
            <p className="text-xs text-gray-500">
              {budget?.number} · {budget?.cliente} {saving && <span className="text-amber-600 ml-2">Guardando…</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportExcel} data-testid="formateo-export-excel-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700">
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Exportar Excel
          </button>
          <button onClick={handleBack} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="formateo-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Bulk toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-3 px-5 py-2 bg-pink-50/60 border-b border-pink-100">
          <span className="text-xs font-semibold text-gray-600">
            {selected.size > 0 ? `${selected.size} seleccionado(s)` : "Selección múltiple"}
          </span>
          <div className="w-64">
            <FrenteSelect frentes={frentes} value={bulkFrente} onChange={setBulkFrente} compact testid="bulk-frente-select" />
          </div>
          <button
            onClick={applyBulk}
            disabled={!bulkFrente || selected.size === 0}
            data-testid="bulk-apply-btn"
            className="px-3 py-1.5 text-xs rounded-lg bg-pink-600 text-white font-semibold disabled:opacity-40 hover:bg-pink-700"
          >
            Aplicar a seleccionados
          </button>
          <div className="ml-auto text-xs text-gray-600">
            <span className="font-semibold text-emerald-700">{assignedCount}</span> con frente ·{" "}
            <span className="font-semibold text-amber-600">{missingCount}</span> sin asignar
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
          </div>
        ) : (
          <table className="w-full text-xs border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                {!readOnly && (
                  <th className="w-8 px-2 py-2">
                    <button onClick={toggleAll} data-testid="select-all-btn">
                      {allSelected ? <CheckSquare className="w-4 h-4 text-pink-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                    </button>
                  </th>
                )}
                {COLS.map((c) => (
                  <th key={c.key} className={`px-2 py-2 text-${c.align} font-bold text-gray-600 uppercase tracking-wide ${c.w}`}>
                    {c.label}
                  </th>
                ))}
                <th className="px-2 py-2 text-left font-bold text-pink-600 uppercase tracking-wide w-[220px]">Frente</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                if (it.is_group) {
                  return (
                    <tr key={idx} className="bg-blue-50/70 font-semibold text-gray-800" data-testid={`formateo-group-row-${idx}`}>
                      {!readOnly && <td className="px-2 py-2" />}
                      <td className="px-2 py-2">{it.numero}</td>
                      <td className="px-2 py-2" colSpan={COLS.length}>{it.descripcion}</td>
                      <td className="px-2 py-2 text-gray-300 text-center">—</td>
                    </tr>
                  );
                }
                return (
                  <tr key={idx} className={`border-t border-gray-50 hover:bg-gray-50/60 ${selected.has(idx) ? "bg-pink-50/40" : ""}`} data-testid={`formateo-item-row-${idx}`}>
                    {!readOnly && (
                      <td className="px-2 py-1.5">
                        <button onClick={() => toggleSel(idx)} data-testid={`row-checkbox-${idx}`}>
                          {selected.has(idx) ? <CheckSquare className="w-4 h-4 text-pink-600" /> : <Square className="w-4 h-4 text-gray-300" />}
                        </button>
                      </td>
                    )}
                    {COLS.map((c) => (
                      <td key={c.key} className={`px-2 py-1.5 text-${c.align} ${c.cur || c.num ? "tabular-nums" : ""} text-gray-700`}>
                        {c.cur ? formatPYG(it[c.key] || 0) : c.key === "descripcion" ? (
                          <span className="whitespace-pre-wrap break-words">{it[c.key]}</span>
                        ) : (it[c.key] ?? "")}
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      {readOnly ? (
                        it.frente_label ? (
                          <span className="inline-block px-2 py-0.5 rounded-md bg-pink-50 border border-pink-200 text-pink-800 text-[11px]" data-testid={`frente-badge-${idx}`}>
                            {it.frente_label}
                          </span>
                        ) : <span className="text-gray-300 text-[11px]">—</span>
                      ) : (
                        <FrenteSelect
                          frentes={frentes}
                          value={it.frente_id ? { frente_id: String(it.frente_id), frente_label: it.frente_label } : null}
                          onChange={(v) => assignOne(idx, v)}
                          compact
                          testid={`frente-select-${idx}`}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-3 bg-white border-t border-gray-200">
        <button onClick={handleBack} className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700" data-testid="formateo-volver-btn">
          {readOnly ? "Volver" : "Cancelar"}
        </button>
        {!readOnly && (
          <button
            onClick={handleCerrar}
            disabled={closing}
            data-testid="formateo-cerrar-btn"
            className="flex items-center gap-2 px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
          >
            {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Cerrar Formato
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
